import { Controller } from "@hotwired/stimulus"

// The workflow's details panel. On a desktop it is the right-hand rail and this
// controller does nothing. Below the desktop breakpoint it is a bottom sheet:
// a paper pill ("Details") opens it, and it slides up over a warm dim, with a
// handle and a close at its head and the assign/state controls pinned to its
// foot. It closes on the close, a tap on the dim, Escape, or a drag down — the
// sheet follows the finger, and a short drag springs back. A drag starts from
// the handle, or anywhere once the sheet is scrolled to its top, so it never
// takes over an ordinary scroll.
//
// Usage: data-controller="toggle-context" with targets content (the panel),
// fab (the pill), backdrop, and handle (the sheet's head).
const CLOSE_DRAG = 90

export default class extends Controller {
  static targets = ["content", "fab", "backdrop", "handle"]

  connect() {
    this.keydown = this.keydown.bind(this)
    this.touchStart = this.touchStart.bind(this)
    this.touchMove = this.touchMove.bind(this)
    this.touchEnd = this.touchEnd.bind(this)
    document.addEventListener("keydown", this.keydown)
    if (this.hasContentTarget) {
      this.contentTarget.addEventListener("touchstart", this.touchStart, { passive: true })
      this.contentTarget.addEventListener("touchmove", this.touchMove, { passive: true })
      this.contentTarget.addEventListener("touchend", this.touchEnd, { passive: true })
    }
    // A page restored from the cache may come back open.
    this.render()
  }

  disconnect() {
    // Turbo can disconnect this while it swaps the page and the document has
    // no body (or, in a test harness, no element at all), so nothing here
    // assumes either is present.
    document.removeEventListener?.("keydown", this.keydown)
    document.documentElement?.classList.remove("context-sheet-open")
    if (this.hasContentTarget) {
      this.contentTarget.removeEventListener("touchstart", this.touchStart)
      this.contentTarget.removeEventListener("touchmove", this.touchMove)
      this.contentTarget.removeEventListener("touchend", this.touchEnd)
    }
  }

  get open() {
    return this.hasContentTarget && this.contentTarget.dataset.visible === "true"
  }

  toggle() {
    this.open ? this.close() : this.show()
  }

  show() {
    this.contentTarget.dataset.visible = "true"
    this.contentTarget.scrollTop = 0
    this.render()
    ;(this.hasHandleTarget ? this.handleTarget.querySelector("button") : this.contentTarget)?.focus({ preventScroll: true })
  }

  close() {
    if (!this.open) return
    this.contentTarget.dataset.visible = "false"
    this.contentTarget.style.translate = ""
    this.render()
    if (this.hasFabTarget) this.fabTarget.focus({ preventScroll: true })
  }

  render() {
    const open = this.open
    if (this.hasBackdropTarget) this.backdropTarget.hidden = !open
    if (this.hasFabTarget) {
      this.fabTarget.hidden = open
      this.fabTarget.setAttribute("aria-expanded", String(open))
    }
    // Only a phone-sized sheet holds the page still behind it.
    document.documentElement.classList.toggle("context-sheet-open", open && window.innerWidth < 1280)
  }

  keydown(event) {
    if (event.key === "Escape" && this.open && window.innerWidth < 1280) this.close()
  }

  touchStart(event) {
    if (!this.open) return
    const fromHandle = this.hasHandleTarget && this.handleTarget.contains(event.target)
    if (!fromHandle && this.contentTarget.scrollTop > 0) {
      this.dragStartY = null
      return
    }
    this.dragStartY = event.touches[0].clientY
    this.dragY = 0
    this.contentTarget.style.transition = "none"
  }

  touchMove(event) {
    if (this.dragStartY == null) return
    this.dragY = Math.max(0, event.touches[0].clientY - this.dragStartY)
    this.contentTarget.style.translate = `0 ${this.dragY}px`
  }

  touchEnd() {
    if (this.dragStartY == null) return
    this.dragStartY = null
    this.contentTarget.style.transition = ""
    if (this.dragY > CLOSE_DRAG) this.close()
    else this.contentTarget.style.translate = ""
  }
}
